import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtDate, fmtDateTime, fmtINR, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { HealthBadge, StatusBadge } from "./Projects";
import { toast } from "sonner";
import { Trash, Plus, PencilSimple, DownloadSimple } from "@phosphor-icons/react";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("overview");

  const reload = () => api.get(`/projects/${id}`).then((r) => setProject(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  if (!project) return <div className="p-10 text-center opacity-60">Loading…</div>;

  const canEdit = user.role === "super_admin" || user.role === "hr" || user.role === "finance_manager"
                  || project.project_manager_id === user.id;

  return (
    <div className="space-y-4">
      <PageHeader title={<span><span className="font-mono text-sm opacity-60 mr-2">{project.code}</span>{project.name}</span>}
                  description={project.customer_name || "No customer"}
                  action={
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => navigate("/projects")}>Back</Button>
                      {canEdit && <Button onClick={() => navigate(`/projects/${id}/edit`)} data-testid="edit-project-btn"><PencilSimple className="mr-1" size={14} /> Edit</Button>}
                    </div>
                  } />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="tab-milestones">Milestones</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-materials">Materials</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><Overview project={project} /></TabsContent>
        <TabsContent value="tasks"><TasksTab projectId={id} /></TabsContent>
        <TabsContent value="milestones"><MilestonesTab projectId={id} canEdit={canEdit} onChange={reload} /></TabsContent>
        <TabsContent value="team"><TeamTab project={project} canEdit={canEdit} onChange={reload} /></TabsContent>
        <TabsContent value="materials"><MaterialsTab projectId={id} canEdit={canEdit} onChange={reload} /></TabsContent>
        <TabsContent value="expenses"><ExpensesTab projectId={id} /></TabsContent>
        <TabsContent value="billing"><BillingTab project={project} canEdit={canEdit} onChange={reload} /></TabsContent>
        <TabsContent value="documents"><DocumentsTab projectId={id} canEdit={canEdit} /></TabsContent>
        <TabsContent value="activity"><ActivityTab projectId={id} /></TabsContent>
      </Tabs>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div><div className="text-[10px] uppercase opacity-60 tracking-wider">{label}</div><div className="text-sm">{value ?? "—"}</div></div>
);

const Overview = ({ project: p }) => (
  <div className="grid lg:grid-cols-3 gap-4 mt-2">
    <Card className="p-4 space-y-3 lg:col-span-2">
      <div className="text-sm font-semibold uppercase opacity-70">Project Information</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Info label="Customer" value={p.customer_name} />
        <Info label="Project Manager" value={p.project_manager_name} />
        <Info label="Project Type" value={p.project_type} />
        <Info label="Priority" value={<span className="capitalize">{p.priority}</span>} />
        <Info label="Status" value={<StatusBadge s={p.status} />} />
        <Info label="Health" value={<HealthBadge h={p.health} />} />
        <Info label="Start Date" value={fmtDate(p.start_date)} />
        <Info label="Expected End" value={fmtDate(p.expected_end_date)} />
        <Info label="Actual Completion" value={fmtDate(p.actual_completion_date)} />
        <Info label="PO Number" value={p.po_number} />
        <Info label="PO Date" value={fmtDate(p.po_date)} />
        <Info label="Contract" value={p.contract_number} />
        <Info label="Location" value={p.location} />
        <Info label="Site Contact" value={`${p.site_contact_name || ""} ${p.site_contact_phone || ""}`.trim()} />
      </div>
      {p.description && <div><div className="text-[10px] uppercase opacity-60 tracking-wider">Description</div><div className="text-sm whitespace-pre-wrap">{p.description}</div></div>}
      {p.site_address && <div><div className="text-[10px] uppercase opacity-60 tracking-wider">Site Address</div><div className="text-sm whitespace-pre-wrap">{p.site_address}</div></div>}
      {p.notes && <div><div className="text-[10px] uppercase opacity-60 tracking-wider">Notes</div><div className="text-sm whitespace-pre-wrap">{p.notes}</div></div>}
    </Card>
    <Card className="p-4 space-y-3">
      <div className="text-sm font-semibold uppercase opacity-70">Progress</div>
      <div>
        <div className="flex justify-between text-xs opacity-70 mb-1"><span>Progress</span><span>{p.progress_pct}%</span></div>
        <div className="bg-slate-100 dark:bg-slate-800 rounded h-2 overflow-hidden">
          <div className="bg-orange-500 h-full" style={{ width: `${p.progress_pct}%` }} />
        </div>
      </div>
      <div className="text-sm font-semibold uppercase opacity-70 mt-4">Commercial</div>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="opacity-60">Value</span><span className="font-mono">{fmtINR(p.project_value)}</span></div>
        <div className="flex justify-between"><span className="opacity-60">Budget</span><span className="font-mono">{fmtINR(p.budget)}</span></div>
        <div className="flex justify-between"><span className="opacity-60">Material Cost</span><span className="font-mono">{fmtINR(p.material_cost)}</span></div>
        <div className="flex justify-between"><span className="opacity-60">Expense Cost</span><span className="font-mono">{fmtINR(p.expense_cost)}</span></div>
        <div className="flex justify-between border-t pt-1"><span className="opacity-60">Total Cost</span><span className="font-mono">{fmtINR(p.total_cost)}</span></div>
        <div className="flex justify-between font-medium"><span>Est. Profit</span>
          <span className={`font-mono ${p.estimated_profit >= 0 ? "text-green-600" : "text-red-600"}`}>{fmtINR(p.estimated_profit)} ({p.profit_margin_pct}%)</span>
        </div>
        <div className="flex justify-between border-t pt-1"><span className="opacity-60">Amount Billed</span><span className="font-mono">{fmtINR(p.amount_billed)}</span></div>
        <div className="flex justify-between"><span className="opacity-60">Amount Received</span><span className="font-mono">{fmtINR(p.amount_received)}</span></div>
        <div className="flex justify-between"><span className="opacity-60">Pending Billing</span><span className="font-mono">{fmtINR(p.pending_billing)}</span></div>
        <div className="flex justify-between"><span className="opacity-60">Outstanding</span><span className="font-mono">{fmtINR(p.outstanding_amount)}</span></div>
      </div>
    </Card>
  </div>
);

const TasksTab = ({ projectId }) => {
  const [tasks, setTasks] = useState([]);
  useEffect(() => { api.get(`/projects/${projectId}/tasks`).then((r) => setTasks(r.data)); }, [projectId]);
  return (
    <Card className="p-4 mt-2">
      <div className="text-sm opacity-60 mb-3">Tasks linked to this project. To create/reassign a task, use the main Tasks page and set the Project field.</div>
      {tasks.length === 0 && <div className="text-xs opacity-60 text-center py-6">No tasks yet.</div>}
      <table className="w-full text-sm">
        <tbody>
        {tasks.map((t) => (
          <tr key={t.id} className="border-b last:border-0">
            <td className="py-2">{t.name}</td>
            <td className="py-2 text-xs opacity-70 capitalize">{(t.status || "").replace(/_/g, " ")}</td>
            <td className="py-2 text-xs">{fmtDate(t.due_date)}</td>
            <td className="py-2 text-xs">{t.progress || 0}%</td>
          </tr>
        ))}
        </tbody>
      </table>
    </Card>
  );
};

const MilestonesTab = ({ projectId, canEdit, onChange }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", target_date: "", completion_pct: 0, status: "pending", billing_pct: 0, remarks: "" });
  const load = () => api.get(`/projects/${projectId}/milestones`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    await api.post(`/projects/${projectId}/milestones`, { ...form, completion_pct: Number(form.completion_pct), billing_pct: Number(form.billing_pct) });
    toast.success("Milestone added"); setOpen(false); setForm({ name: "", target_date: "", completion_pct: 0, status: "pending", billing_pct: 0, remarks: "" }); load(); onChange();
  };
  const update = async (m, patch) => {
    await api.put(`/projects/${projectId}/milestones/${m.id}`, patch);
    toast.success("Updated"); load(); onChange();
  };
  const del = async (m) => {
    if (!window.confirm("Delete milestone?")) return;
    await api.delete(`/projects/${projectId}/milestones/${m.id}`); load(); onChange();
  };
  return (
    <Card className="p-4 mt-2">
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm opacity-60">Project milestones. Progress on completed milestones rolls up to Project Progress.</div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-milestone-btn"><Plus className="mr-1" size={14} /> Milestone</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Milestone</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Target Date</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></div>
                  <div><Label>Status</Label><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["pending", "in_progress", "completed", "delayed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select></div>
                  <div><Label>Completion %</Label><Input type="number" value={form.completion_pct} onChange={(e) => setForm({ ...form, completion_pct: e.target.value })} /></div>
                  <div><Label>Billing %</Label><Input type="number" value={form.billing_pct} onChange={(e) => setForm({ ...form, billing_pct: e.target.value })} /></div>
                </div>
                <div><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={submit}>Add</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {items.length === 0 && <div className="text-xs opacity-60 text-center py-6">No milestones yet.</div>}
      <div className="space-y-2">
        {items.map((m) => (
          <div key={m.id} className="border rounded-md p-3">
            <div className="flex items-center justify-between">
              <div className="font-medium">{m.name} <span className="text-xs opacity-60 ml-2">Target: {fmtDate(m.target_date) || "—"}</span></div>
              <div className="flex items-center gap-2">
                <StatusBadge s={m.status} />
                {canEdit && <>
                  <Select value={m.status} onValueChange={(v) => update(m, { status: v })}>
                    <SelectTrigger className="w-32 h-7 text-xs" data-testid={`ms-status-${m.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["pending", "in_progress", "completed", "delayed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" onClick={() => del(m)}><Trash size={14} /></Button>
                </>}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs">
              <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded h-2 overflow-hidden"><div className="bg-orange-500 h-full" style={{ width: `${m.completion_pct}%` }} /></div>
              <div className="w-10 text-right">{m.completion_pct}%</div>
              <div className="opacity-60">Billing: {m.billing_pct}%</div>
            </div>
            {m.remarks && <div className="text-xs opacity-70 mt-1">{m.remarks}</div>}
          </div>
        ))}
      </div>
    </Card>
  );
};

const TeamTab = ({ project, canEdit, onChange }) => {
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", role_in_project: "project_engineer", allocation_pct: 100, status: "active" });
  useEffect(() => {
    api.get("/employees/lookup/all").then((r) => setEmployees(r.data || []));
    api.get("/projects/statuses").then((r) => setRoles(r.data.team_roles || []));
  }, []);
  const submit = async () => {
    if (!form.employee_id) { toast.error("Select employee"); return; }
    await api.post(`/projects/${project.id}/team`, { ...form, allocation_pct: Number(form.allocation_pct) });
    toast.success("Added"); setOpen(false); onChange();
  };
  const remove = async (emp_id) => {
    if (!window.confirm("Remove from team?")) return;
    await api.delete(`/projects/${project.id}/team/${emp_id}`); onChange();
  };
  return (
    <Card className="p-4 mt-2">
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm opacity-60">Team members assigned to this project.</div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-team-btn"><Plus className="mr-1" size={14} /> Member</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Employee</Label><Select value={form.employee_id || "__none__"} onValueChange={(v) => setForm({ ...form, employee_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">— Select —</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                </Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Role</Label><Select value={form.role_in_project} onValueChange={(v) => setForm({ ...form, role_in_project: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select></div>
                  <div><Label>Allocation %</Label><Input type="number" value={form.allocation_pct} onChange={(e) => setForm({ ...form, allocation_pct: e.target.value })} /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={submit}>Add</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/40"><tr>{["Employee", "Role", "Allocation", "Assigned On", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>
          {(project.members || []).length === 0 && <tr><td colSpan="5" className="text-center py-6 opacity-60">No team members yet.</td></tr>}
          {(project.members || []).map((m) => (
            <tr key={m.employee_id} className="border-t">
              <td className="px-3 py-2">{m.employee_name || m.employee_id}</td>
              <td className="px-3 py-2 text-xs capitalize">{(m.role_in_project || "").replace(/_/g, " ")}</td>
              <td className="px-3 py-2 text-xs">{m.allocation_pct}%</td>
              <td className="px-3 py-2 text-xs">{fmtDate(m.assigned_on)}</td>
              <td className="px-3 py-2">{canEdit && <Button variant="ghost" size="icon" onClick={() => remove(m.employee_id)}><Trash size={14} /></Button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};

const MaterialsTab = ({ projectId, canEdit, onChange }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ material_name: "", required_qty: 0, issued_qty: 0, consumed_qty: 0, returned_qty: 0, unit_cost: 0, remarks: "" });
  const load = () => api.get(`/projects/${projectId}/materials`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  const submit = async () => {
    if (!form.material_name.trim()) { toast.error("Name required"); return; }
    const p = { ...form }; ["required_qty", "issued_qty", "consumed_qty", "returned_qty", "unit_cost"].forEach((k) => p[k] = Number(p[k] || 0));
    await api.post(`/projects/${projectId}/materials`, p);
    toast.success("Added"); setOpen(false); setForm({ material_name: "", required_qty: 0, issued_qty: 0, consumed_qty: 0, returned_qty: 0, unit_cost: 0, remarks: "" }); load(); onChange();
  };
  const del = async (m) => {
    if (!window.confirm("Delete material line?")) return;
    await api.delete(`/projects/${projectId}/materials/${m.id}`); load(); onChange();
  };
  return (
    <Card className="p-4 mt-2 overflow-x-auto">
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm opacity-60">Materials required, issued, consumed and returned for this project.</div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-material-btn"><Plus className="mr-1" size={14} /> Material</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Material</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Material Name *</Label><Input value={form.material_name} onChange={(e) => setForm({ ...form, material_name: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><Label>Required</Label><Input type="number" value={form.required_qty} onChange={(e) => setForm({ ...form, required_qty: e.target.value })} /></div>
                  <div><Label>Issued</Label><Input type="number" value={form.issued_qty} onChange={(e) => setForm({ ...form, issued_qty: e.target.value })} /></div>
                  <div><Label>Consumed</Label><Input type="number" value={form.consumed_qty} onChange={(e) => setForm({ ...form, consumed_qty: e.target.value })} /></div>
                  <div><Label>Returned</Label><Input type="number" value={form.returned_qty} onChange={(e) => setForm({ ...form, returned_qty: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Unit Cost (₹)</Label><Input type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></div>
                </div>
                <div><Label>Remarks</Label><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={submit}>Add</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/40"><tr>{["Material", "Required", "Issued", "Consumed", "Returned", "Pending", "Unit Cost", "Total", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan="9" className="text-center py-6 opacity-60">No materials yet.</td></tr>}
          {items.map((m) => {
            const pending = (m.required_qty || 0) - (m.issued_qty || 0);
            const total = (m.consumed_qty || m.issued_qty || 0) * (m.unit_cost || 0);
            return (
              <tr key={m.id} className="border-t">
                <td className="px-3 py-2">{m.material_name}</td>
                <td className="px-3 py-2 text-xs">{m.required_qty}</td>
                <td className="px-3 py-2 text-xs">{m.issued_qty}</td>
                <td className="px-3 py-2 text-xs">{m.consumed_qty}</td>
                <td className="px-3 py-2 text-xs">{m.returned_qty}</td>
                <td className="px-3 py-2 text-xs">{pending}</td>
                <td className="px-3 py-2 text-xs font-mono">{fmtINR(m.unit_cost)}</td>
                <td className="px-3 py-2 text-xs font-mono">{fmtINR(total)}</td>
                <td className="px-3 py-2">{canEdit && <Button variant="ghost" size="icon" onClick={() => del(m)}><Trash size={14} /></Button>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
};

const ExpensesTab = ({ projectId }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/projects/${projectId}/expenses`).then((r) => setItems(r.data)); }, [projectId]);
  return (
    <Card className="p-4 mt-2 overflow-x-auto">
      <div className="text-sm opacity-60 mb-3">Approved & paid expenses linked to this project. Add a project when filing a new expense claim to see it here.</div>
      {items.length === 0 && <div className="text-xs opacity-60 text-center py-6">No expenses linked yet.</div>}
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/40"><tr>{["Date", "Category / Type", "Amount", "Status", "Description"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>
          {items.map((e, i) => (
            <tr key={i} className="border-t">
              <td className="px-3 py-2 text-xs">{fmtDate(e.expense_date || e.created_at)}</td>
              <td className="px-3 py-2 text-xs capitalize">{((e.category || e.expense_type || "").replace(/_/g, " "))}</td>
              <td className="px-3 py-2 text-xs font-mono">{fmtINR(e.amount || e.total_amount)}</td>
              <td className="px-3 py-2 text-xs capitalize">{e.status}</td>
              <td className="px-3 py-2 text-xs opacity-70">{e.description || e.expense_name || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};

const BillingTab = ({ project, canEdit, onChange }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [form, setForm] = useState({ milestone_id: "", amount: 0, invoice_number: "", invoice_date: "", received_amount: 0, received_on: "", notes: "" });
  const load = () => api.get(`/projects/${project.id}/billing`).then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get(`/projects/${project.id}/milestones`).then((r) => setMilestones(r.data));
    /* eslint-disable-next-line */
  }, [project.id]);
  const submit = async () => {
    const p = { ...form, amount: Number(form.amount), received_amount: Number(form.received_amount) };
    if (p.amount <= 0) { toast.error("Amount required"); return; }
    if (!p.milestone_id) delete p.milestone_id;
    await api.post(`/projects/${project.id}/billing`, p);
    toast.success("Bill added"); setOpen(false); setForm({ milestone_id: "", amount: 0, invoice_number: "", invoice_date: "", received_amount: 0, received_on: "", notes: "" });
    load(); onChange();
  };
  const del = async (b) => {
    if (!window.confirm("Delete bill?")) return;
    await api.delete(`/projects/${project.id}/billing/${b.id}`); load(); onChange();
  };
  return (
    <Card className="p-4 mt-2">
      <div className="flex justify-between items-center mb-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Info label="Project Value" value={fmtINR(project.project_value)} />
          <Info label="Amount Billed" value={fmtINR(project.amount_billed)} />
          <Info label="Amount Received" value={fmtINR(project.amount_received)} />
          <Info label="Pending Billing" value={fmtINR(project.pending_billing)} />
          <Info label="Outstanding" value={fmtINR(project.outstanding_amount)} />
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-billing-btn"><Plus className="mr-1" size={14} /> Bill</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Billing Record</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Milestone (optional)</Label><Select value={form.milestone_id || "__none__"} onValueChange={(v) => setForm({ ...form, milestone_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Not linked" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">— None —</SelectItem>{milestones.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Amount (₹) *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
                  <div><Label>Invoice Number</Label><Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} /></div>
                  <div><Label>Invoice Date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
                  <div><Label>Received Amount</Label><Input type="number" value={form.received_amount} onChange={(e) => setForm({ ...form, received_amount: e.target.value })} /></div>
                  <div><Label>Received On</Label><Input type="date" value={form.received_on} onChange={(e) => setForm({ ...form, received_on: e.target.value })} /></div>
                </div>
                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={submit}>Add</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/40"><tr>{["Invoice #", "Date", "Milestone", "Amount", "Received", "Outstanding", "Notes", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan="8" className="text-center py-6 opacity-60">No bills yet.</td></tr>}
          {items.map((b) => {
            const ms = milestones.find((m) => m.id === b.milestone_id);
            return (
              <tr key={b.id} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">{b.invoice_number || "—"}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(b.invoice_date)}</td>
                <td className="px-3 py-2 text-xs">{ms?.name || "—"}</td>
                <td className="px-3 py-2 text-xs font-mono">{fmtINR(b.amount)}</td>
                <td className="px-3 py-2 text-xs font-mono">{fmtINR(b.received_amount)}</td>
                <td className="px-3 py-2 text-xs font-mono">{fmtINR((b.amount || 0) - (b.received_amount || 0))}</td>
                <td className="px-3 py-2 text-xs opacity-70">{b.notes}</td>
                <td className="px-3 py-2">{canEdit && <Button variant="ghost" size="icon" onClick={() => del(b)}><Trash size={14} /></Button>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
};

const DocumentsTab = ({ projectId, canEdit }) => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dlgCat, setDlgCat] = useState("other");
  const [dlgName, setDlgName] = useState("");
  const [pendingFileId, setPendingFileId] = useState(null);
  const load = () => api.get(`/projects/${projectId}/documents`).then((r) => setItems(r.data));
  useEffect(() => {
    load(); api.get("/projects/statuses").then((r) => setCategories(r.data.document_categories));
    /* eslint-disable-next-line */
  }, [projectId]);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingFileId(r.data.file_id); setDlgName(f.name);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!pendingFileId) return;
    await api.post(`/projects/${projectId}/documents`, { file_id: pendingFileId, document_name: dlgName, category: dlgCat });
    toast.success("Document added"); setPendingFileId(null); setDlgName(""); setDlgCat("other"); load();
  };
  const del = async (d) => {
    if (!window.confirm("Delete document?")) return;
    await api.delete(`/projects/${projectId}/documents/${d.id}`); load();
  };
  return (
    <Card className="p-4 mt-2">
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm opacity-60">Project documents (PO, contract, quotation, drawings, certificates …)</div>
        {canEdit && (
          <label className="inline-flex">
            <input type="file" hidden onChange={onFile} disabled={uploading} />
            <Button size="sm" asChild disabled={uploading}><span><Plus className="mr-1" size={14} /> {uploading ? "Uploading…" : "Upload"}</span></Button>
          </label>
        )}
      </div>
      <Dialog open={Boolean(pendingFileId)} onOpenChange={(v) => !v && setPendingFileId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Document Details</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Document Name</Label><Input value={dlgName} onChange={(e) => setDlgName(e.target.value)} /></div>
            <div><Label>Category</Label><Select value={dlgCat} onValueChange={setDlgCat}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select></div>
          </div>
          <DialogFooter><Button onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {items.length === 0 && <div className="text-xs opacity-60 text-center py-6">No documents yet.</div>}
      <div className="grid md:grid-cols-2 gap-2">
        {items.map((d) => (
          <div key={d.id} className="flex items-center justify-between border rounded-md p-3">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{d.document_name}</div>
              <div className="text-xs opacity-60 capitalize">{d.category.replace(/_/g, " ")} • {d.uploaded_by_name} • {fmtDate(d.created_at)}</div>
            </div>
            <div className="flex gap-1">
              <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer">
                <Button variant="ghost" size="icon"><DownloadSimple size={16} /></Button>
              </a>
              {canEdit && <Button variant="ghost" size="icon" onClick={() => del(d)}><Trash size={14} /></Button>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const ActivityTab = ({ projectId }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/projects/${projectId}/activity`).then((r) => setItems(r.data)); }, [projectId]);
  return (
    <Card className="p-4 mt-2">
      {items.length === 0 && <div className="text-xs opacity-60 text-center py-6">No activity yet.</div>}
      <ul className="space-y-2">
        {items.map((a) => (
          <li key={a.id} className="text-sm border-l-2 border-orange-500 pl-3 py-1">
            <span className="font-medium">{a.actor_name}</span>{" "}
            <span className="opacity-70">{(a.action || "").replace(/_/g, " ")}</span>
            {a.detail && <span className="opacity-70"> — {a.detail}</span>}
            <div className="text-xs opacity-50">{fmtDateTime(a.timestamp)}</div>
          </li>
        ))}
      </ul>
    </Card>
  );
};
